/**
 * pd-fairing-kit.js — THEME-SIDE COPY of the app block's fairing-kit.js.
 *
 * Why a copy: the production home of this widget is the theme app extension
 * (web/extensions/fitment-widgets/assets/fairing-kit.js). An app block cannot appear in a theme
 * until `shopify app deploy` has run, and that command also ships the OTHER extension changes in
 * this checkout, which the LIVE theme uses. So that deploy cannot happen just to preview this.
 * This copy lets Parts Denmark 0.2 show the real widget against the real API with zero live
 * impact. Delete it once the app version is deployed and the block is added in the editor.
 *
 * KEEP IN SYNC with the extension asset. The only intended difference is relocate() below.
 *
 * fairing-kit.js — the accessory-system band in the product buy zone.
 *
 * A fairing is sold as a SYSTEM: the look, the bike-specific mount kit, and often a third part
 * a supplier footnote requires. Everything comes from /api/storefront/fairing-kit, which serves
 * the supplier's own fitment charts; the product's own fitments cannot answer "fits my bike".
 * See FAIRING_KITS.md.
 *
 * Standalone: it shares only the Garage vehicle state (localStorage `fitment_garage` +
 * `fitment_garage_active`, event `fitment:garage-updated`) with the rest of the site.
 *
 * Two things worth knowing before editing:
 *
 *  1. The rows LOOK like the theme's variant pickers on purpose, but they are not variants.
 *     Each row is a separate product with its own stock and price. Never wire them into the
 *     product form's variantId; the button posts several line items to /cart/add.js.
 *
 *  2. The qualifier question ("does your bike have a light bar?") is a property of the
 *     customer's MOTORCYCLE, not of this purchase, so the answer is saved onto the bike in the
 *     garage. Asked once, remembered for every later visit and every other product.
 */
(function () {
  "use strict";

  var GARAGE_KEY = "fitment_garage";
  var GARAGE_ACTIVE_KEY = "fitment_garage_active";
  var SKUS_CACHE_KEY = "pd_fk_skus"; // sessionStorage: which SKUs have a system at all

  // --- i18n (self-contained, like service-kit.js; app blocks cannot read theme locale keys) --
  var STRINGS = {
    en: {
      cardTitle: "Complete your kit",
      completeFor: "Completes your {system} for {vehicle}",
      fitsWith: "Fits your {vehicle} with these {system} options",
      completeNoBike: "Complete your {system} with the right mount kit for your bike",
      selectBike: "Select bike",
      mount: "Mount kit",
      plates: "Plates only",
      shield: "Windshield",
      fairing: "Fairing",
      lowers: "Lowers",
      deflector: "Deflectors",
      handguard: "Hand guards",
      ring: "Tank ring",
      carrier: "Side carrier",
      bag: "Bags",
      sideLeft: "Left side",
      sideRight: "Right side",
      carrier: "Side carrier",
      carrierLeft: "Side carrier left",
      carrierRight: "Side carrier right",
      haveIt: "Have it",
      haveCarrier: "Already have one",
      haveNothing: "Nothing",
      none: "None",
      noneChosen: "None chosen",
      thisItem: "This item",
      fits: "Fits",
      plate: "Plate",
      modeBag: "Bag + adapter plate",
      modePlate: "Have the bag · plate only",
      onCarrier: "What goes on the carrier?",
      fitsBike: "Fits your {vehicle}",
      carrierWord: "side carrier",
      mount: "Mount",
      bagLabel: "Bag",
      add: "Add",
      added: "✓ Added",
      pairBoth: "Pair · left + right",
      fitsYourBike: "Fits your bike",
      // SW-Motech systems, keyed by the server's systemKey. Named after what the customer is
      // buying on the page: the bag, whose tank ring or carrier the card then offers.
      sys: {
        "pro-tank-ring": "PRO tank bag",
        "ion-tank-ring": "ION tank bag",
        "evo-tank-ring": "EVO tank bag",
        "slc-left": "SLC side bag (left)",
        "slc-right": "SLC side bag (right)",
        "slh-lh1-left": "LH1 side bag (left)",
        "slh-lh1-right": "LH1 side bag (right)",
        "slh-lh2-left": "LH2 side bag (left)",
        "slh-lh2-right": "LH2 side bag (right)",
        "pro-side-carrier": "PRO side luggage",
        "evo-side-carrier": "EVO side luggage",
      },
      alsoNeeded: "Also needed",
      withKit: "With kit",
      kitAdded: "Your kit is added with the product below",
      failed: "Could not add the kit to your cart",
      noMount: "This {system} has no mount kit for your {vehicle}.",
      qLightbar: "Does your bike have a light bar?",
      qLightbarWith: "With light bar",
      qLightbarWithout: "Without light bar",
      qForks: "Are your forks covered or exposed?",
      qForksCovered: "Covered forks",
      qForksExposed: "Exposed forks",
      qVariant: "Which version is your bike?",
      change: "Change",
      outOfStock: "Out of stock",
      backorder: "On backorder",
      backorderNote: "Note: {items} is on backorder and ships as soon as it arrives.",
      noKit: "None",
      summary: "Your set",
    },
    da: {
      cardTitle: "Saml dit sæt",
      completeFor: "Komplet din {system} til {vehicle}",
      fitsWith: "Passer til din {vehicle} med disse {system}-muligheder",
      completeNoBike: "Komplet din {system} med det rigtige monteringskit til din motorcykel",
      selectBike: "Vælg motorcykel",
      mount: "Monteringskit",
      plates: "Kun plader",
      shield: "Forrude",
      fairing: "Kåbe",
      lowers: "Underskærme",
      deflector: "Vindafvisere",
      handguard: "Håndbeskyttere",
      ring: "Tankring",
      carrier: "Sidebærer",
      bag: "Tasker",
      sideLeft: "Venstre side",
      sideRight: "Højre side",
      carrier: "Sidebærer",
      carrierLeft: "Sidebærer venstre",
      carrierRight: "Sidebærer højre",
      haveIt: "Har den",
      haveCarrier: "Har allerede",
      haveNothing: "Har intet",
      none: "Ingen",
      noneChosen: "Ingen valgt",
      thisItem: "Denne vare",
      fits: "Passer",
      plate: "Plade",
      modeBag: "Taske + adapterplade",
      modePlate: "Har tasken · kun plade",
      onCarrier: "Hvad skal på bæreren?",
      fitsBike: "Passer til din {vehicle}",
      carrierWord: "sidebærer",
      mount: "Beslag",
      bagLabel: "Taske",
      add: "Tilføj",
      added: "✓ Tilføjet",
      pairBoth: "2 stk. · venstre + højre",
      fitsYourBike: "Passer til din MC",
      sys: {
        "pro-tank-ring": "PRO-tanktaske",
        "ion-tank-ring": "ION-tanktaske",
        "evo-tank-ring": "EVO-tanktaske",
        "slc-left": "SLC-sidetaske (venstre)",
        "slc-right": "SLC-sidetaske (højre)",
        "slh-lh1-left": "LH1-sidetaske (venstre)",
        "slh-lh1-right": "LH1-sidetaske (højre)",
        "slh-lh2-left": "LH2-sidetaske (venstre)",
        "slh-lh2-right": "LH2-sidetaske (højre)",
        "pro-side-carrier": "PRO-sidebagage",
        "evo-side-carrier": "EVO-sidebagage",
      },
      alsoNeeded: "Kræves også",
      withKit: "Med sæt",
      kitAdded: "Dit sæt lægges i kurven sammen med varen nedenfor",
      failed: "Kunne ikke lægge sættet i kurven",
      noMount: "Denne {system} har intet monteringskit til din {vehicle}.",
      qLightbar: "Har din motorcykel en lygtebøjle?",
      qLightbarWith: "Med lygtebøjle",
      qLightbarWithout: "Uden lygtebøjle",
      qForks: "Er dine gafler dækkede eller udækkede?",
      qForksCovered: "Dækkede gafler",
      qForksExposed: "Udækkede gafler",
      qVariant: "Hvilken version er din motorcykel?",
      change: "Skift",
      outOfStock: "Ikke på lager",
      backorder: "I restordre",
      backorderNote: "Vær opmærksom: {items} er i restordre og sendes så snart den er hjemme.",
      noKit: "Ingen",
      summary: "Dit sæt",
    },
  };

  function pickStrings(locale) {
    var lang = String(locale || "en").slice(0, 2).toLowerCase();
    return STRINGS[lang] || STRINGS.en;
  }
  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] == null ? "" : vars[k];
    });
  }
  /**
   * The supplier writes its own US suggested retail into the footnote
   * ("… PART # MEM8984 SUG. RETAIL $29.95"). Showing a dollar price next to our own price is
   * confusing, so it is stripped for display only. The raw text stays in the database.
   */
  function cleanNote(text) {
    return String(text || "")
      .replace(/,?\s*SUG\.?\s*RETAIL\s*\$[\d.,]+/gi, "")
      .replace(/\s{2,}/g, " ")
      .replace(/\s+([.,;])/g, "$1")
      .trim();
  }
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function money(amount, currency, locale) {
    var n = Number(amount);
    if (!isFinite(n)) return "";
    try {
      return new Intl.NumberFormat(locale || "en", { style: "currency", currency: currency || "DKK" }).format(n);
    } catch (e) {
      return (n.toFixed(2) + " " + (currency || "")).trim();
    }
  }

  // --- garage ---------------------------------------------------------------------------------

  function garageAll() {
    try {
      var all = JSON.parse(localStorage.getItem(GARAGE_KEY) || "[]");
      return Array.isArray(all) ? all : [];
    } catch (e) {
      return [];
    }
  }
  function activeKey() {
    try {
      return localStorage.getItem(GARAGE_ACTIVE_KEY) || "";
    } catch (e) {
      return "";
    }
  }
  function getActiveVehicle() {
    var key = activeKey();
    if (!key) return null;
    return garageAll().find(function (v) {
      return v.year + "|" + v.make + "|" + v.model === key;
    }) || null;
  }

  /**
   * Remember the customer's answer ON THE BIKE, so it is never asked twice. Silently does
   * nothing if storage is unavailable — a forgotten answer costs one extra tap, a thrown
   * exception costs the whole band.
   */
  function rememberQualifier(questionKey, value) {
    try {
      var key = activeKey();
      if (!key) return;
      var all = garageAll();
      var bike = all.find(function (v) {
        return v.year + "|" + v.make + "|" + v.model === key;
      });
      if (!bike) return;
      bike.accQual = bike.accQual || {};
      bike.accQual[questionKey] = value;
      localStorage.setItem(GARAGE_KEY, JSON.stringify(all));
    } catch (e) {
      /* storage full or blocked — the question is simply asked again */
    }
  }
  function recallQualifier(questionKey) {
    var bike = getActiveVehicle();
    return (bike && bike.accQual && bike.accQual[questionKey]) || null;
  }

  // --- api ------------------------------------------------------------------------------------

  function apiBase(root) {
    return root.dataset.apiBase || "https://pd-garage.fly.dev";
  }
  async function apiFetch(root, params) {
    var base = apiBase(root);
    var url = new URL("/api/storefront/fairing-kit", base.endsWith("/") ? base.slice(0, -1) : base);
    Object.keys(params).forEach(function (k) {
      var v = params[k];
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    });
    var res = await fetch(url.toString());
    if (!res.ok) throw new Error("fairing-kit " + res.status);
    return res.json();
  }

  /**
   * Which SKUs have a system at all. Asked once per session and cached, so a product page for
   * something unrelated makes no request and the buy zone never moves.
   */
  async function knownSkus(root) {
    try {
      var cached = sessionStorage.getItem(SKUS_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) {
      /* fall through and fetch */
    }
    var data = await apiFetch(root, { shop: root.dataset.shop, mode: "skus" });
    delete data.storefrontToken;
    delete data.storefrontApiVersion;
    try {
      sessionStorage.setItem(SKUS_CACHE_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode — we just refetch next page */
    }
    return data;
  }

  // --- market-correct pricing ------------------------------------------------------------
  //
  // products_cache holds the shop's base DKK amount. Formatting that with the page's currency
  // code prints "EUR 1,469" for a 1,469 kr part, which is wrong by a factor of seven. Same
  // @inContext approach as service-kit.js and the product feed; the cached amount is only the
  // fallback when there is no token.

  var SF_PRICE_QUERY =
    "query P($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){ nodes(ids:$ids){ ... on ProductVariant { id price{ amount currencyCode } } } }";

  async function loadMarketPrices(data, shop, country, extraIds) {
    if (!data || !data.storefrontToken || !shop || !country) return null;
    var rows = [].concat(data.items || [], data.alsoNeeded || []).filter(function (i) { return i.variantId; });
    var ids = [...new Set(rows.map(function (i) { return i.variantId; }).concat(extraIds || []))];
    if (!ids.length) return null;
    var market = new Map();
    try {
      for (var i = 0; i < ids.length; i += 200) {
        var res = await fetch("https://" + shop + "/api/" + (data.storefrontApiVersion || "2025-01") + "/graphql.json", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": data.storefrontToken },
          body: JSON.stringify({
            query: SF_PRICE_QUERY,
            variables: { ids: ids.slice(i, i + 200).map(function (v) { return "gid://shopify/ProductVariant/" + v; }), country: country },
          }),
        });
        if (!res.ok) continue;
        var body = await res.json();
        ((body && body.data && body.data.nodes) || []).forEach(function (n) {
          if (!n || !n.id || !n.price) return;
          var m = String(n.id).match(/(\d+)$/);
          if (m) market.set(Number(m[1]), { amount: Number(n.price.amount), currency: n.price.currencyCode });
        });
      }
    } catch (e) {
      return null;
    }
    if (!market.size) return null;
    var ccy = null;
    rows.forEach(function (r) {
      var mk = market.get(r.variantId);
      if (!mk || mk.amount == null) return;
      r.price = mk.amount;
      ccy = ccy || mk.currency;
    });
    return { currency: ccy, market: market };
  }

  /** The variant the customer is actually looking at, read from the theme's own product form. */
  function currentVariantId() {
    try {
      var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var v = input && Number(input.value);
      return v && isFinite(v) ? v : null;
    } catch (e) {
      return null;
    }
  }

  // --- rendering --------------------------------------------------------------------------------

  var ROLE_ORDER = ["mount", "plates", "ring", "carrier", "fairing", "shield", "lowers", "deflector", "handguard", "bag"];

  function questionCopy(S, q) {
    if (q.key === "lightbar") {
      return {
        title: S.qLightbar,
        label: function (o) { return o.key === "with" ? S.qLightbarWith : S.qLightbarWithout; },
      };
    }
    if (q.key === "forks") {
      return {
        title: S.qForks,
        label: function (o) { return o.key === "covered" ? S.qForksCovered : S.qForksExposed; },
      };
    }
    return { title: S.qVariant, label: function (o) { return o.value; } };
  }

  /** Ask Shopify's CDN for a small square instead of the full-size product photo. */
  function thumb(url, px) {
    if (!url) return "";
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set("width", String(px || 240));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * One option's short label: the finish or the height where there is one, else the product
   * name with the brand stripped. A 62px tile cannot hold "Memphis Shades Batwing Fairing",
   * and the brand is already obvious from the page you are standing on.
   */
  function optionLabel(item, brand) {
    if (item.finish || item.height || item.tint) return item.finish || item.height || item.tint;
    var t = String(item.title || "");
    if (brand) {
      var words = String(brand).replace(/_/g, "[\\s_-]+");
      t = t.replace(new RegExp("^\\s*" + words + "\\s+", "i"), "");
    }
    return t;
  }

  function renderQuestion(S, q) {
    var copy = questionCopy(S, q);
    return (
      '<div class="pd-fk__q">' +
      '<p class="pd-fk__q-title">' + escapeHtml(copy.title) + "</p>" +
      '<div class="pd-fk__q-opts">' +
      q.options
        .map(function (o) {
          return (
            '<button type="button" class="pd-fk__q-opt" data-fk-answer="' + escapeHtml(o.value) + '">' +
            escapeHtml(copy.label(o)) +
            "</button>"
          );
        })
        .join("") +
      "</div></div>"
    );
  }

  /**
   * The bracket rows as 1:1 image tiles with the finish written underneath, so the customer can
   * SEE polished against black rather than read the words. Starts with a tile carrying a red
   * corner-to-corner stroke: the mount kit is optional, and someone who already owns the
   * bracket must be able to say so instead of being forced to buy it again.
   */
  function renderTiles(S, role, items, currency, locale, brand) {
    // Nothing is pre-selected. The customer came for the product; the bracket is an addition
    // they opt INTO, so "None" leads the row and starts chosen.
    var tiles = items
      .map(function (it) {
        var on = false;
        return (
          '<button type="button" class="pd-fk__tile' + (on ? " is-on" : "") + '"' +
          ' data-fk-pick="' + escapeHtml(role) + '"' +
          ' data-var="' + (it.variantId || "") + '"' +
          ' data-price="' + escapeHtml(it.price) + '"' +
          ' data-sku="' + escapeHtml(it.sku) + '"' +
          ' data-title="' + escapeHtml(it.title) + '"' +
          ' data-vendor="' + escapeHtml(it.vendorPart || "") + '"' +
          ' data-href="' + escapeHtml(it.handle ? "/products/" + it.handle : "") + '"' +
          // Not disabled when it is out of stock: the whole shop sells on backorder, so the
          // only thing missing was saying so (merchant 2026-09-24).
          ' data-bo="' + (it.available ? "" : "1") + '"' +
          ' title="' + escapeHtml(it.title) + '">' +
          '<span class="pd-fk__tile-art">' +
          (it.image ? '<img src="' + escapeHtml(thumb(it.image, 240)) + '" alt="" loading="lazy">' : "") +
          "</span>" +
          '<span class="pd-fk__tile-label">' + escapeHtml(optionLabel(it, brand)) + "</span>" +
          '<span class="pd-fk__tile-price">' + escapeHtml(money(it.price, currency, locale)) +
          (it.available ? "" : '<span class="pd-fk__tile-bo">' + escapeHtml(S.backorder) + "</span>") +
          "</span></button>"
        );
      })
      .join("");
    var none =
      '<button type="button" class="pd-fk__tile pd-fk__tile--none is-on"' +
      ' data-fk-pick="' + escapeHtml(role) + '" data-var="" data-price="0">' +
      '<span class="pd-fk__tile-art" aria-hidden="true"></span>' +
      '<span class="pd-fk__tile-label">' + escapeHtml(S.noKit) + "</span>" +
      // an empty price line, so this tile has the same structure as the others and all three
      // labels sit on one baseline
      '<span class="pd-fk__tile-price"></span></button>';
    return (
      '<div class="pd-fk__row pd-fk__row--tiles" data-fk-row="' + escapeHtml(role) + '">' +
      '<span class="pd-fk__label">' + escapeHtml(S[role] || role) + "</span>" +
      // None sits FIRST: the opt-out reads as a starting point, not an afterthought appended
      // to the options. The pre-selection is still the first available kit, not this tile.
      '<div class="pd-fk__tiles">' + none + tiles + "</div></div>"
    );
  }

  /** A variant-style row: label, pills, price of the selected pill. */
  function renderRow(S, role, items, currency, locale, brand) {
    var label = S[role] || role;
    var pills = items
      .map(function (it) {
        // Same as the tiles: backorder is a delivery time, not a reason to refuse the sale.
        return (
          '<button type="button" class="pd-fk__pill"' +
          ' data-fk-pick="' + escapeHtml(role) + '"' +
          ' data-var="' + (it.variantId || "") + '"' +
          ' data-price="' + escapeHtml(it.price) + '"' +
          ' data-sku="' + escapeHtml(it.sku) + '"' +
          ' data-title="' + escapeHtml(it.title) + '"' +
          ' data-vendor="' + escapeHtml(it.vendorPart || "") + '"' +
          ' data-href="' + escapeHtml(it.handle ? "/products/" + it.handle : "") + '"' +
          ' data-bo="' + (it.available ? "" : "1") + '">' +
          escapeHtml(optionLabel(it, brand)) +
          (it.available ? "" : ' <span class="pd-fk__oos">' + escapeHtml(S.backorder) + "</span>") +
          "</button>"
        );
      })
      .join("");
    return (
      '<div class="pd-fk__row" data-fk-row="' + escapeHtml(role) + '">' +
      '<span class="pd-fk__label">' + escapeHtml(label) + "</span>" +
      '<div class="pd-fk__pills">' + pills + "</div>" +
      '<span class="pd-fk__price" data-fk-price></span>' +
      "</div>"
    );
  }

  /**
   * A required accessory reads as a choice, not as a checkbox tucked under the options
   * (merchant 2026-09-24: "så skal det vises præcis som hvis du skal vælge en kåbe").
   * It IS the same choice — take it or don't — so it renders through renderTiles: same label,
   * same "Ingen" tile, same picture and price. One row per item, because two accessories are
   * two independent yes/no answers, not a pick-one.
   *
   * Nothing else changed: the tiles carry data-fk-pick, which chosenRows(), extras() and the
   * cart hand-off already read. The hidden checkbox they used to need is gone.
   */
  function renderExtra(S, items, currency, locale, brand) {
    return items
      .map(function (it) { return renderTiles(S, "alsoNeeded", [it], currency, locale, brand); })
      .join("");
  }

  /**
   * The set at a glance: name, the manufacturer's part number, price. Styled like the theme's
   * own Product info card. Both the name and the number link to the product, so a customer can
   * check a bracket on its own page before committing to the whole set.
   */
  function renderSummary(S, rows, currency, locale, sum) {
    // Always return the wrapper, even with nothing in it: syncTotal() replaces this node in
    // place, and an empty string would delete the anchor it needs next time.
    if (!rows.length) return '<div data-fk-sum></div>';
    return (
      '<div class="pd-fk__sum" data-fk-sum>' +
      '<p class="pd-fk__sum-title">' + escapeHtml(S.summary) + "</p>" +
      '<table class="pd-fk__sum-tbl"><tbody>' +
      rows
        .map(function (r) {
          var name = r.href
            ? '<a href="' + escapeHtml(r.href) + '">' + escapeHtml(r.title) + "</a>"
            : escapeHtml(r.title);
          var part = r.vendor
            ? r.href
              ? '<a href="' + escapeHtml(r.href) + '">' + escapeHtml(r.vendor) + "</a>"
              : escapeHtml(r.vendor)
            : "";
          return (
            "<tr><td class=\"pd-fk__sum-name\">" + name + "</td>" +
            '<td class="pd-fk__sum-part">' + part + "</td>" +
            '<td class="pd-fk__sum-price">' + escapeHtml(money(r.price, currency, locale)) + "</td></tr>"
          );
        })
        .join("") +
      // The total used to sit beside the product price, above the whole card. It belongs at the
      // foot of the set it sums, with a rule above it (merchant 2026-09-24).
      '<tr class="pd-fk__sum-total"><th colspan="2">' + escapeHtml(S.withKit) + "</th>" +
      '<td class="pd-fk__sum-price">' + escapeHtml(money(sum, currency, locale)) + "</td></tr>" +
      "</tbody></table></div>"
    );
  }


  // --- SW-Motech side luggage: one section per side, each with its mount and its bag ------------
  //
  // A side-luggage build is two independent sides on top of a carrier, and each side is either a
  // bag with its adapter plate or just the plate for someone who owns the bag. Merchant
  // 2026-09-26: lay it out the way a rider thinks about it — "left side: mount, this bag; right
  // side: mount, bag" — with the whole set OFFERED, never forced: nothing is chosen except the
  // product on the page, and every mount is a small "Add" line, not a big tile.
  //
  // Every side has its own mount line. A PRO/EVO carrier is a KIT for both sides ("2 x PRO side
  // carrier"), so its two lines move together: add it on one side and it shows added on both
  // (merchant 2026-09-26), bought once (onPicks()). SLC/SLH carriers are sold per side and each
  // side's line is independent. Audited over the whole file: every PRO/EVO carrier is a kit,
  // every SLC/SLH carrier is one side, no exceptions. Every choice is still a [data-fk-row] of [data-fk-pick] elements, so
  // the summary, the total and the ride-along on the theme's Add to cart are unchanged.

  /** A bag tile. `self` = the product being viewed: shown as chosen, never picked. */
  function sideTile(S, it, opts, currency, locale) {
    var name = it.kind === "plate" ? S.plate + " " + String(it.tileName || "").replace(/^SysBag\s+/i, "") : it.tileName || it.title;
    var chosen = volumeText(it.volume, locale) || name;
    var attrs = opts.self
      ? ' aria-current="true"'
      : ' data-fk-pick="' + escapeHtml(opts.row) + '"' +
        ' data-var="' + (it.variantId || "") + '"' +
        ' data-price="' + escapeHtml(it.price) + '"' +
        ' data-sku="' + escapeHtml(it.sku) + '"' +
        ' data-title="' + escapeHtml(it.title) + '"' +
        ' data-vendor="' + escapeHtml(it.vendorPart || "") + '"' +
        ' data-href="' + escapeHtml(it.handle ? "/products/" + it.handle : "") + '"' +
        ' data-bo="' + (it.available ? "" : "1") + '"' +
        ' data-chosen="' + escapeHtml(chosen) + '"';
    return (
      '<button type="button" class="pd-fk__gt' + (opts.self ? " is-on is-self" : "") + '"' + attrs +
      ' title="' + escapeHtml(it.title || name) + '">' +
      '<span class="pd-fk__gt-art">' +
      (it.image ? '<img src="' + escapeHtml(thumb(it.image, 240)) + '" alt="" loading="lazy">' : "") +
      "</span>" +
      '<span class="pd-fk__gt-name">' + escapeHtml(name) + "</span>" +
      '<span class="pd-fk__gt-price">' +
      (opts.self ? escapeHtml(opts.selfPrice) : escapeHtml(money(it.price, currency, locale))) +
      (!opts.self && !it.available ? ' <span class="pd-fk__gt-bo">' + escapeHtml(S.backorder) + "</span>" : "") +
      "</span>" +
      (opts.tag ? '<span class="pd-fk__gt-tag">' + escapeHtml(opts.tag) + "</span>" : "") +
      "</button>"
    );
  }

  /** A bag volume in the page's number style: "9.8 l" reads "9,8 l" in Danish. */
  function volumeText(v, locale) {
    if (!v) return "";
    return /^en/i.test(String(locale || "")) ? String(v) : String(v).replace(/(\d)\.(\d)/g, "$1,$2");
  }

  /** "PRO side carrier" -> "PRO sidebærer": SW's English names in the shop's language. */
  function carrierName(S, c) {
    return String(c.tileName || c.title || "").replace(/\bside carriers?\b/i, S.carrierWord);
  }

  /**
   * The mount as one compact line with an Add button. Opt-in: a hidden "none" pick starts
   * chosen, and clicking a chosen mount again hands the row back to it (see the click handler).
   */
  function carrierRow(S, key, list, pair, currency, locale) {
    var cards = list
      .map(function (c) {
        return (
          '<button type="button" class="pd-fk__cr"' +
          ' data-fk-pick="' + escapeHtml(key) + '"' +
          ' data-var="' + (c.variantId || "") + '"' +
          ' data-price="' + escapeHtml(c.price) + '"' +
          ' data-sku="' + escapeHtml(c.sku) + '"' +
          ' data-title="' + escapeHtml(c.title) + '"' +
          ' data-vendor="' + escapeHtml(c.vendorPart || "") + '"' +
          ' data-href="' + escapeHtml(c.handle ? "/products/" + c.handle : "") + '"' +
          ' data-bo="' + (c.available ? "" : "1") + '">' +
          '<span class="pd-fk__cr-art">' +
          (c.image ? '<img src="' + escapeHtml(thumb(c.image, 160)) + '" alt="" loading="lazy">' : "") +
          "</span>" +
          '<span class="pd-fk__cr-txt"><span class="pd-fk__cr-name">' + escapeHtml(carrierName(S, c)) + "</span>" +
          '<span class="pd-fk__cr-meta">' + escapeHtml(pair ? S.pairBoth : S.fitsYourBike) +
          " · " + escapeHtml(money(c.price, currency, locale)) +
          (!c.available ? " · " + escapeHtml(S.backorder) : "") + "</span></span>" +
          '<span class="pd-fk__cr-act"><span class="pd-fk__cr-off">' + escapeHtml(S.add) + "</span>" +
          '<span class="pd-fk__cr-on">' + escapeHtml(S.added) + "</span></span>" +
          "</button>"
        );
      })
      .join("");
    return (
      '<div class="pd-fk__crs" data-fk-row="' + escapeHtml(key) + '" data-fk-toggle' + (pair ? " data-fk-pair" : "") + ">" +
      '<button type="button" class="pd-fk__cr-none is-on" data-fk-pick="' + escapeHtml(key) + '" data-var="" data-price="0" hidden></button>' +
      cards + "</div>"
    );
  }

  /**
   * @param {object} d      the band payload (layout "sides")
   * @param {string} mode   "bag" | "plate"
   * @param {string} selfPriceText the viewed product's price, already formatted
   */
  function renderSides(S, d, mode, currency, locale, selfPriceText) {
    var sidesShown = d.showSides || ["left", "right"];
    var carriers = d.carriers || [];
    var html = "";

    sidesShown.forEach(function (side) {
      var parts = "";

      // 1. the mount for this side
      if (carriers.length) {
        if (d.carrierSplit) {
          var mine = carriers.filter(function (c) { return c.side === side; });
          if (mine.length) {
            parts += '<p class="pd-fk__sublabel">' + escapeHtml(S.mount) + "</p>" +
              carrierRow(S, "carrier-" + side, mine, false, currency, locale);
          }
        } else {
          // sold as a pair: offered on each side, bought once however many sides want it
          parts += '<p class="pd-fk__sublabel">' + escapeHtml(S.mount) + "</p>" +
            carrierRow(S, "carrier-" + side, carriers, true, currency, locale);
        }
      }

      // 2. the bag for this side
      var key = "side-" + side;
      if (d.selfRole === "bag" && d.selfSide === side) {
        // The product on the page IS this side's bag. The theme's own button adds it.
        var me = { tileName: d.selfTileName, kind: d.selfKind, image: d.selfImage, title: d.selfTileName, volume: d.selfVolume };
        parts +=
          '<div class="pd-fk__srow">' +
          '<div class="pd-fk__shead"><span class="pd-fk__sublabel">' + escapeHtml(d.selfKind === "plate" ? S.plate : S.bagLabel) + "</span>" +
          '<span class="pd-fk__schosen">' + escapeHtml(volumeText(d.selfVolume, locale)) + "</span></div>" +
          '<div class="pd-fk__grid">' + sideTile(S, me, { self: true, tag: S.thisItem, selfPrice: selfPriceText }, currency, locale) + "</div></div>";
      } else {
        var list = ((d.sides && d.sides[side]) || []).filter(function (i) { return i.kind === mode; });
        if (list.length) {
          parts +=
            '<div class="pd-fk__srow" data-fk-row="' + key + '">' +
            '<div class="pd-fk__shead"><span class="pd-fk__sublabel">' + escapeHtml(mode === "plate" ? S.plate : S.bagLabel) + "</span>" +
            '<span class="pd-fk__schosen" data-fk-chosen>' + escapeHtml(S.noneChosen) + "</span></div>" +
            '<div class="pd-fk__grid">' +
            '<button type="button" class="pd-fk__gt pd-fk__gt--none is-on" data-fk-pick="' + key + '" data-var="" data-price="0" data-chosen="' + escapeHtml(S.noneChosen) + '">' +
            '<span class="pd-fk__gt-art" aria-hidden="true"></span>' +
            '<span class="pd-fk__gt-name">' + escapeHtml(S.none) + "</span>" +
            '<span class="pd-fk__gt-price"></span></button>' +
            list.map(function (i) { return sideTile(S, i, { row: key }, currency, locale); }).join("") +
            "</div></div>";
        }
      }
      if (!parts) return;
      html +=
        '<section class="pd-fk__side">' +
        '<p class="pd-fk__slabel">' + escapeHtml(side === "left" ? S.sideLeft : S.sideRight) + "</p>" +
        parts + "</section>";
    });
    return html;
  }

  function mount(root) {
    var S = pickStrings(root.dataset.locale);
    var currency = root.dataset.currency;
    var selfPrice = 0; // the viewed product, in the market's currency
    var locale = root.dataset.locale;
    var body = root.querySelector("[data-fk-body]");
    var sku = root.dataset.sku;
    var state = { data: null, chosen: {}, mode: null };

    function show(html) {
      // One heading for the whole card, in every state, so the box does not change shape as the
      // customer picks a bike and the content grows.
      body.innerHTML = '<p class="pd-fk__card-title">' + escapeHtml(S.cardTitle) + "</p>" + html;
      root.hidden = false;
    }
    function hide() {
      root.hidden = true;
      body.innerHTML = "";
    }

    /**
     * The chosen options, each product once. A SW pair carrier is offered under both sides;
     * ticking it on both must still put ONE in the cart, one line in the summary, one price.
     */
    function onPicks() {
      var seen = {};
      return Array.prototype.filter.call(root.querySelectorAll("[data-fk-pick].is-on"), function (p) {
        if (!p.dataset.var) return true;
        if (seen[p.dataset.var]) return false;
        seen[p.dataset.var] = true;
        return true;
      });
    }

    function total() {
      // The viewed product is part of the set, so the button's amount and its cart action
      // both include it. Without that, "Add complete setup" would quietly omit the fairing.
      var sum = selfPrice || 0;
      onPicks().forEach(function (p) {
        sum += Number(p.dataset.price) || 0;
      });
      root.querySelectorAll("[data-fk-extra]:checked").forEach(function (c) {
        sum += Number(c.dataset.price) || 0;
      });
      return sum;
    }
    /** The product being viewed, first row of the summary and first line in the cart. */
    function selfRow() {
      if (!root.dataset.selfTitle) return null;
      return {
        title: root.dataset.selfTitle,
        vendor: root.dataset.selfVendor || "",
        href: root.dataset.selfUrl || "",
        price: selfPrice || 0,
      };
    }

    function chosenRows() {
      var rows = [];
      var me = selfRow();
      if (me) rows.push(me);
      onPicks().forEach(function (p) {
        if (!p.dataset.var) return; // the opt-out tile is not part of the set
        rows.push({ title: p.dataset.title, vendor: p.dataset.vendor, href: p.dataset.href, price: Number(p.dataset.price) || 0 });
      });
      root.querySelectorAll("[data-fk-extra]:checked").forEach(function (c) {
        if (!c.dataset.var) return;
        rows.push({ title: c.dataset.title, vendor: c.dataset.vendor, href: c.dataset.href, price: Number(c.dataset.price) || 0 });
      });
      return rows;
    }

    /** Everything the customer has added on top of the product itself. */
    function extras() {
      var items = [];
      onPicks().forEach(function (p) {
        if (p.dataset.var) items.push(p);
      });
      root.querySelectorAll("[data-fk-extra]:checked").forEach(function (c) {
        if (c.dataset.var) items.push(c);
      });
      return items;
    }

    function syncTotal() {
      var sum = root.querySelector("[data-fk-sum]");
      if (sum) sum.outerHTML = renderSummary(S, chosenRows(), currency, locale, total());
      var n = extras().length;
      var note = root.querySelector("[data-fk-with]");
      if (note) {
        note.textContent = S.kitAdded;
        note.hidden = !n;
      }
      // Only what the customer has actually chosen — a backordered option they passed over is
      // not their problem.
      var bo = root.querySelector("[data-fk-bo]");
      if (bo) {
        var late = [];
        onPicks().filter(function (p) { return p.dataset.bo === "1"; }).forEach(function (p) {
          if (p.dataset.var) late.push(p.dataset.title);
        });
        bo.textContent = late.length ? fill(S.backorderNote, { items: late.join(", ") }) : "";
        bo.hidden = !late.length;
      }
      // No echo beside the product price any more — one number, at the foot of the card.
      var stale = document.querySelector("[data-pd-fk-echo]");
      if (stale) stale.remove();
    }

    /** SW side luggage: draw (or redraw after a bag/plate switch) and keep what was chosen. */
    function showSides() {
      var d = state.data;
      var keep = {};
      root.querySelectorAll("[data-fk-row]").forEach(function (row) {
        var on = row.querySelector("[data-fk-pick].is-on");
        if (on && on.dataset.var) keep[row.dataset.fkRow] = on.dataset.var;
      });
      if (!selfPrice && d.selfPrice) selfPrice = Number(d.selfPrice) || 0;
      var mode = state.mode || (d.selfKind === "plate" ? "plate" : "bag");
      state.mode = mode;
      var selfPriceText = selfPrice ? money(selfPrice, currency, locale) : "";
      var head = d.selfRole === "carrier"
        ? '<p class="pd-fk__head pd-fk__head--q">' + escapeHtml(S.onCarrier) + "</p>" +
          '<p class="pd-fk__sub">' + escapeHtml(fill(S.fitsBike, { vehicle: d.vehicle })) + "</p>"
        : '<p class="pd-fk__sub">' + escapeHtml(fill(S.fitsBike, { vehicle: d.vehicle })) + "</p>";
      var modeBar = d.hasPlates
        ? '<div class="pd-fk__mode" role="group">' +
          '<button type="button" data-fk-mode="bag" class="' + (mode === "bag" ? "is-on" : "") + '" aria-pressed="' + (mode === "bag") + '">' + escapeHtml(S.modeBag) + "</button>" +
          '<button type="button" data-fk-mode="plate" class="' + (mode === "plate" ? "is-on" : "") + '" aria-pressed="' + (mode === "plate") + '">' + escapeHtml(S.modePlate) + "</button>" +
          "</div>"
        : "";
      show(
        head + modeBar +
        '<div class="pd-fk__sides">' + renderSides(S, d, mode, currency, locale, selfPriceText) + "</div>" +
        (d.note ? '<p class="pd-fk__note">' + escapeHtml(cleanNote(d.note)) + "</p>" : "") +
        '<div data-fk-sum></div>' +
        '<p class="pd-fk__bo" data-fk-bo hidden></p>' +
        '<p class="pd-fk__with" data-fk-with hidden></p>',
      );
      // put back what the customer had chosen, where that option still exists in this mode
      Object.keys(keep).forEach(function (rowKey) {
        var row = root.querySelector('[data-fk-row="' + rowKey + '"]');
        var tile = row && row.querySelector('[data-fk-pick][data-var="' + keep[rowKey] + '"]');
        if (!tile) return;
        row.querySelectorAll("[data-fk-pick]").forEach(function (p) { p.classList.remove("is-on"); });
        tile.classList.add("is-on");
        var ch = row.querySelector("[data-fk-chosen]");
        if (ch) ch.textContent = tile.dataset.chosen || "";
      });
      syncTotal();
    }

    async function load() {
      var bike = getActiveVehicle();
      var params = { shop: root.dataset.shop, sku: sku };
      if (bike) {
        params.year = bike.year;
        params.make = bike.make;
        params.model = bike.model;
        if (bike.vehicleType) params.vehicleType = bike.vehicleType;
      }
      // Send a previously remembered answer straight away, so a returning customer is not
      // asked the same thing about the same motorcycle twice.
      var remembered = recallQualifier("lightbar") || recallQualifier("forks");
      if (remembered) params.qualifier = remembered;
      // Supplier notes (SW-Motech's "Tank bag is not in the middle.") come back translated.
      if (locale) params.locale = locale;

      var data;
      try {
        data = await apiFetch(root, params);
      } catch (e) {
        hide();
        return;
      }
      state.data = data;
      // Side luggage sends its options grouped by side; the price lookup below reads `items`,
      // so point it at the SAME objects the side rows render from.
      if (data.layout === "sides") {
        data.items = [].concat(data.carriers || [], (data.sides && data.sides.left) || [], (data.sides && data.sides.right) || []);
      }

      var selfVar = currentVariantId();
      var priced = await loadMarketPrices(data, root.dataset.shop, root.dataset.country, selfVar ? [selfVar] : []);
      if (priced && priced.currency) currency = priced.currency;
      selfPrice = priced && selfVar && priced.market.get(selfVar) ? priced.market.get(selfVar).amount : 0;

      if (!data.available) return hide();
      // The supplier lists this product for other bikes, not this one. The fit card above
      // already says so; offering a bracket for it would be a wrong recommendation.
      if (data.reason === "not_for_vehicle") return hide();

      var sysName = (S.sys && S.sys[data.systemKey]) || data.systemLabel || "";
      if (!data.vehicle) {
        show(
          '<p class="pd-fk__oneline">' +
          escapeHtml(fill(S.completeNoBike, { system: sysName })) +
          ' <button type="button" class="pd-fk__link" data-fk-find>' + escapeHtml(S.selectBike) + "</button></p>",
        );
        return;
      }
      if (data.question) {
        show(
          '<p class="pd-fk__head">' + escapeHtml(fill(S.completeFor, { system: sysName, vehicle: data.vehicle })) + "</p>" +
          renderQuestion(S, data.question),
        );
        return;
      }
      if (data.layout === "sides") return data.items.length ? showSides() : hide();
      // SW-Motech names a carrier for this bike that we do not stock: saying "no mount kit"
      // would be false, and the fit card above already carries the note. Stay out of the way.
      if (data.brand === "sw_motech" && (!data.items || !data.items.length)) return hide();
      if (!data.items || !data.items.length) {
        show('<p class="pd-fk__oneline">' + escapeHtml(fill(S.noMount, { system: sysName, vehicle: data.vehicle })) + "</p>");
        return;
      }

      var byRole = {};
      data.items.forEach(function (it) {
        (byRole[it.role] = byRole[it.role] || []).push(it);
      });
      var rows = ROLE_ORDER.filter(function (r) { return byRole[r]; })
        .map(function (r) {
          // Anything with a photo becomes tiles, in both directions: on a bracket page the
          // fairings it carries read the same way the brackets do on a fairing page. Heights
          // and tints stay pills, where the label carries the meaning and the photos do not.
          var tileable = r !== "shield" && byRole[r].some(function (i) { return i.image; });
          return tileable
            ? renderTiles(S, r, byRole[r], currency, locale, data.brand)
            : renderRow(S, r, byRole[r], currency, locale, data.brand);
        })
        .join("");

      show(
        '<p class="pd-fk__head">' +
        escapeHtml(fill(data.direction === "mount_to_look" ? S.fitsWith : S.completeFor, { system: sysName, vehicle: data.vehicle })) +
        "</p>" +
        rows +
        (data.alsoNeeded && data.alsoNeeded.length ? renderExtra(S, data.alsoNeeded, currency, locale, data.brand) : "") +
        '<div data-fk-sum></div>' +
        (data.note ? '<p class="pd-fk__note">' + escapeHtml(cleanNote(data.note)) + "</p>" : "") +
        // Named so the customer is not surprised at the door: the set can be bought, one part
        // of it just arrives later.
        '<p class="pd-fk__bo" data-fk-bo hidden></p>' +
        // No button of our own: the theme's Add to cart takes the whole set. See attachToCart().
        '<p class="pd-fk__with" data-fk-with hidden></p>',
      );
      syncTotal();
    }

    /**
     * Ride along with the theme's own Add to cart instead of offering a second button.
     *
     * Two buttons in one buy zone is a way to sell the same fairing twice. So: catch the click
     * in the CAPTURE phase, put our extras in the cart first, then re-fire it with a
     * pass-through flag so the theme adds the product and opens its drawer exactly as it
     * normally would. We never reimplement the theme's cart behaviour, we just go first.
     *
     * One listener on the document rather than one per button: this product page carries THREE
     * add-to-cart buttons (main, sticky, and a hidden one), and the theme re-renders them on
     * every variant change. Hooking a single element covered one of the three and silently
     * missed the rest.
     */
    function attachToCart() {
      if (document.documentElement.dataset.pdFkCartHook) return;
      document.documentElement.dataset.pdFkCartHook = "1";
      document.addEventListener(
        "click",
        function (ev) {
          var btn = ev.target.closest && ev.target.closest('[type="submit"], .product-form__submit, button[name="add"]');
          if (!btn || !btn.closest('form[action*="/cart/add"]')) return;
          if (btn.dataset.pdFkPass) {
            delete btn.dataset.pdFkPass;
            return; // our own re-fire; let the theme have it
          }
          var picks = extras();
          if (!picks.length) return; // nothing of ours — the theme proceeds untouched
          ev.preventDefault();
          ev.stopPropagation();
          var items = picks.map(function (p) { return { id: Number(p.dataset.var), quantity: 1 }; });
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: items }),
          })
            .catch(function () {
              var n = root.querySelector("[data-fk-with]");
              if (n) { n.textContent = S.failed; n.hidden = false; }
            })
            .then(function () {
              // Hand back to the theme whether or not we succeeded: failing to add a bracket
              // must never cost the customer the product they actually came for.
              btn.dataset.pdFkPass = "1";
              btn.click();
            });
        },
        true,
      );
    }

    root.addEventListener("click", function (ev) {
      var modeBtn = ev.target.closest("[data-fk-mode]");
      if (modeBtn) {
        state.mode = modeBtn.dataset.fkMode;
        showSides();
        return;
      }
      var pill = ev.target.closest("[data-fk-pick]");
      if (pill && pill.classList.contains("is-on") && pill.dataset.var) {
        var toggleRow = pill.closest("[data-fk-toggle]");
        var off = toggleRow && toggleRow.querySelector('[data-fk-pick][data-var=""]');
        if (off) pill = off;
      }
      if (pill && !pill.disabled) {
        var row = pill.closest("[data-fk-row]");
        row.querySelectorAll("[data-fk-pick]").forEach(function (p) { p.classList.remove("is-on"); });
        pill.classList.add("is-on");
        if (row.hasAttribute("data-fk-pair")) {
          root.querySelectorAll("[data-fk-pair]").forEach(function (other) {
            if (other === row) return;
            var twin = other.querySelector('[data-fk-pick][data-var="' + (pill.dataset.var || "") + '"]');
            if (!twin) return;
            other.querySelectorAll("[data-fk-pick]").forEach(function (p) { p.classList.remove("is-on"); });
            twin.classList.add("is-on");
          });
        }
        var chosen = row.querySelector("[data-fk-chosen]");
        if (chosen) chosen.textContent = pill.dataset.chosen || "";
        // Tile rows carry the price under each tile, so there is no row-level cell to update.
        var price = row.querySelector("[data-fk-price]");
        if (price) price.textContent = pill.dataset.var ? money(pill.dataset.price, currency, locale) : "";
        syncTotal();
        return;
      }
      var answer = ev.target.closest("[data-fk-answer]");
      if (answer) {
        var q = state.data && state.data.question;
        if (q) rememberQualifier(q.key, answer.dataset.fkAnswer);
        load();
        return;
      }
      var find = ev.target.closest("[data-fk-find]");
      if (find) {
        var trig = document.querySelector("[data-gw-open]");
        if (trig) {
          try { trig.click(); return; } catch (e) { /* fall through */ }
        }
        window.location.href = "/pages/find-dele";
      }
    });

    root.addEventListener("change", function (ev) {
      if (ev.target.closest("[data-fk-extra]")) syncTotal();
    });

    window.addEventListener("fitment:garage-updated", load);
    attachToCart();
    load();
  }

  /**
   * Theme-only: the section renders at the bottom of the product template, so move the band
   * into the buy zone, directly under the price. Falls back to leaving it where it is rather
   * than throwing, because a missing price block must not cost us the whole widget.
   */
  function relocate(root) {
    try {
      var price = document.querySelector(".product__block--price");
      if (!price || !price.parentNode) return;
      price.parentNode.insertBefore(root, price.nextSibling);
    } catch (e) {
      /* leave it where the section rendered it */
    }
  }

  async function init() {
    var roots = document.querySelectorAll("[data-pd-fairing-kit]");
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.dataset.fkReady) continue;
      root.dataset.fkReady = "1";
      if (!root.dataset.sku) continue;
      relocate(root);
      try {
        var skus = await knownSkus(root);
        // Silent on every product that has no accessory system — no request, no layout shift.
        if (!skus || !skus[root.dataset.sku]) continue;
      } catch (e) {
        continue;
      }
      mount(root);
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
